import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Play, X, ExternalLink, Check, Sparkles, Users, GraduationCap,
  ClipboardCheck, CalendarClock, Wallet, Bot, MessageSquare, LineChart,
  Shield, Cloud, Zap, School, BookOpen, Building2, Lightbulb,
} from "lucide-react";

const YT_ID = "JuvgmUPWr58";
const YT_EMBED = `https://www.youtube-nocookie.com/embed/${YT_ID}?rel=0&modestbranding=1&playsinline=1`;
const YT_PREVIEW = `https://www.youtube-nocookie.com/embed/${YT_ID}?autoplay=1&mute=1&loop=1&playlist=${YT_ID}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1`;
const LAUNCH_URL = "https://apps.sms.zerolabsdigi.in/";

const FEATURES = [
  { icon: Users, title: "Student & Staff Management", desc: "Complete profiles, class assignments, transfers and alumni records in one secure directory." },
  { icon: ClipboardCheck, title: "Attendance powered by AI", desc: "Face-recognition & pattern-detection attendance with instant parent alerts on absentees." },
  { icon: CalendarClock, title: "Timetable & Exam Modules", desc: "Smart timetable generator, exam scheduling, hall tickets, evaluation and grade cards." },
  { icon: Wallet, title: "Online Admissions & Fees", desc: "Digitised enquiries, fee reminders, receipts, and instant payment integrations." },
  { icon: Bot, title: "AI Tutor for Students", desc: "Personalised doubt-solving, chapter revision, quiz generation and study plans — 24×7." },
  { icon: Lightbulb, title: "AI Guidance for Teachers", desc: "Lesson-plan drafts, question-paper generation, and student performance insights." },
  { icon: MessageSquare, title: "Parent App & Notifications", desc: "Push, SMS and WhatsApp notifications for attendance, fees, exams and homework." },
  { icon: LineChart, title: "Analytics Dashboards", desc: "Class-wise, student-wise and school-wide analytics — attendance trends, exam heatmaps and fee collection." },
];

const AUDIENCES = [
  { icon: School, title: "K-12 Schools", desc: "From LKG to 12th — one platform for admissions, academics and communication." },
  { icon: GraduationCap, title: "Coaching Institutes", desc: "Batch-based scheduling, mock tests, doubt-solving with the built-in AI tutor." },
  { icon: BookOpen, title: "Colleges & Junior Colleges", desc: "Multi-stream departments, semester exams and attendance across campuses." },
  { icon: Building2, title: "School Chains", desc: "Multi-branch dashboards, franchise reporting and centralised policies." },
];

const WHY_US = [
  { icon: Shield, title: "Secure & Compliant", desc: "Role-based access, audited data trails and encrypted parent-teacher communication." },
  { icon: Cloud, title: "Cloud-Native", desc: "Zero installation. Access from any device — desktop, tablet, phone." },
  { icon: Zap, title: "AI-First, Not Bolted-On", desc: "AI Tutor, AI teacher guidance and AI attendance built into the core — not add-ons." },
  { icon: LineChart, title: "Actionable Analytics", desc: "See what matters at a glance — collection %, attendance dips, at-risk students." },
];

const FAQ = [
  { q: "Can we migrate from our existing ERP?", a: "Yes. We provide guided data migration for students, staff, fees and marks. Most schools migrate in 3–7 days with zero downtime." },
  { q: "How does the AI Tutor work?", a: "Every enrolled student gets a personal AI tutor trained on your curriculum (CBSE / ICSE / State board). It answers subject doubts, generates quizzes and revision notes in the student's language." },
  { q: "Do parents need a separate app?", a: "Parents receive attendance / fees / exam updates via WhatsApp, SMS and a lightweight parent portal — no forced app download required." },
  { q: "Is my data safe?", a: "All data is encrypted at rest and in transit. We are hosted on enterprise cloud with daily backups. Role-based access ensures teachers, parents and admins see only what they should." },
  { q: "What's the setup timeline?", a: "Typical rollout: 3–7 days for a single school, 2–4 weeks for a chain of 5+ branches. We handle configuration, training and data import." },
  { q: "Can we get a live demo?", a: "Yes — click 'Try Live Demo' above or book a call and one of our specialists will walk your leadership team through the platform." },
];

export default function SchoolManagementSystem() {
  const [openVideo, setOpenVideo] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    document.title = "School Management System — ZeroLabs Digitals";
  }, []);

  return (
    <div data-testid="page-sms-landing" className="pt-24 pb-24">
      {/* ---------- Hero ---------- */}
      <section className="relative overflow-hidden">
        <div className="orb orb-purple" style={{ top: "-100px", right: "-60px", width: "400px", height: "400px" }} />
        <div className="orb orb-orange" style={{ top: "160px", left: "-80px", width: "300px", height: "300px", opacity: 0.4 }} />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="eyebrow inline-flex items-center gap-2">
              <Sparkles size={12}/> ZeroLabs Product · Live
            </div>
            <nav className="mt-3 text-xs text-[#8C859E] font-mono" aria-label="Breadcrumb">
              <Link to="/products" className="hover:text-[#3B82F6]">Products</Link>
              <span className="mx-2">/</span>
              <span className="text-[#22D3EE]">School Management System</span>
            </nav>
            <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]">
              <span className="text-gradient">One-stop AI platform</span> to run your school end-to-end
            </h1>
            <p className="mt-5 text-lg text-[#D6D2E1] max-w-xl leading-relaxed">
              From admissions and attendance to AI tutoring, exams, fees and analytics — ZeroLabs
              School Management System unifies every workflow in a single cloud platform, trusted
              by schools and institutes across India.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a data-testid="sms-launch-btn" href={LAUNCH_URL} target="_blank" rel="noreferrer" className="btn-primary">
                Try Live Demo <ExternalLink size={16}/>
              </a>
              <button data-testid="sms-watch-btn" onClick={() => setOpenVideo(true)} className="btn-secondary backdrop-blur-md">
                <Play size={14}/> Watch 90-sec walkthrough
              </button>
            </div>
            <div className="mt-6 flex items-center gap-6 text-xs text-[#8C859E]">
              <div className="flex items-center gap-2"><Check size={14} className="text-[#22D3EE]"/> No credit card</div>
              <div className="flex items-center gap-2"><Check size={14} className="text-[#22D3EE]"/> Guided migration</div>
              <div className="flex items-center gap-2"><Check size={14} className="text-[#22D3EE]"/> India-hosted</div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="absolute -inset-4 bg-[radial-gradient(circle_at_30%_30%,rgba(59,130,246,0.22),transparent_60%)] blur-2xl"/>
            <button onClick={() => setOpenVideo(true)} className="relative w-full rounded-2xl overflow-hidden border border-white/10 group text-left" aria-label="Play demo video">
              <iframe
                src={YT_PREVIEW}
                title="School Management System demo preview"
                data-testid="sms-hero-video"
                className="w-full h-[420px] pointer-events-none"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08060F] via-transparent to-transparent pointer-events-none"/>
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                <span className="w-20 h-20 rounded-full bg-[#3B82F6] text-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                  <Play size={32} fill="currentColor"/>
                </span>
              </div>
              <div className="absolute top-4 left-4 pointer-events-none">
                <span className="px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.2em] border bg-[#22D3EE]/15 text-[#22D3EE] border-[#22D3EE]/40">Live · in production</span>
              </div>
              <div className="absolute bottom-4 left-4 pointer-events-none">
                <span className="px-2 py-1 rounded-md bg-[#08060F]/80 border border-white/10 text-xs font-mono text-[#22D3EE]">apps.sms.zerolabsdigi.in</span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* ---------- Trust metrics ---------- */}
      <section className="mt-16 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "1+", v: "Schools onboarded" },
            { k: "1,000+", v: "Students & staff records" },
            { k: "99.9%", v: "Uptime SLA" },
            { k: "24×7", v: "AI Tutor availability" },
          ].map((s) => (
            <div key={s.v} className="card p-6 text-center">
              <div className="text-3xl md:text-4xl font-extrabold text-gradient">{s.k}</div>
              <div className="mt-1 text-xs text-[#B6B0C7] uppercase tracking-[0.2em]">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Features grid ---------- */}
      <section id="features" className="mt-24 max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="eyebrow">Every workflow, one platform</div>
          <h2 className="mt-2 text-3xl md:text-5xl font-bold text-white tracking-tight">Built for the full academic year</h2>
          <p className="mt-3 text-[#B6B0C7]">Nine deeply-connected modules — no more juggling spreadsheets, WhatsApp groups and paper registers.</p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sms-features-grid">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="card p-6 group hover:border-[#3B82F6]/40 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] flex items-center justify-center group-hover:bg-[#3B82F6]/20 transition-colors">
                <Icon size={20}/>
              </div>
              <div className="mt-4 text-white font-semibold">{title}</div>
              <div className="mt-1 text-sm text-[#B6B0C7] leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Why us ---------- */}
      <section className="mt-24 max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <div className="eyebrow">Why schools choose ZeroLabs</div>
          <h2 className="mt-2 text-3xl md:text-5xl font-bold text-white tracking-tight">A modern platform for a modern school</h2>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHY_US.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="card p-6">
              <div className="w-10 h-10 rounded-lg bg-[#22D3EE]/10 text-[#22D3EE] flex items-center justify-center"><Icon size={20}/></div>
              <div className="mt-4 text-white font-semibold">{title}</div>
              <div className="mt-1 text-sm text-[#B6B0C7] leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Audiences ---------- */}
      <section className="mt-24 max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="eyebrow">Who it's for</div>
          <h2 className="mt-2 text-3xl md:text-5xl font-bold text-white tracking-tight">Designed for every kind of institute</h2>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {AUDIENCES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="card p-6">
              <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] flex items-center justify-center"><Icon size={20}/></div>
              <div className="mt-4 text-white font-semibold">{title}</div>
              <div className="mt-1 text-sm text-[#B6B0C7] leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- AI Highlight strip ---------- */}
      <section className="mt-24 max-w-7xl mx-auto px-6 md:px-10">
        <div className="card p-8 md:p-12 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-[0.09]" aria-hidden="true" style={{
            backgroundImage: "radial-gradient(circle at 12% 50%, rgba(59,130,246,0.6) 0, transparent 30%), radial-gradient(circle at 88% 50%, rgba(34,211,238,0.5) 0, transparent 35%)",
          }}/>
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="eyebrow inline-flex items-center gap-2"><Bot size={12}/> AI at the core</div>
              <h2 className="mt-2 text-3xl md:text-4xl font-bold text-white tracking-tight">Your school gets three AI teammates on day one.</h2>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#3B82F6]/15 text-[#3B82F6] flex items-center justify-center shrink-0"><Bot size={16}/></div>
                  <div>
                    <div className="text-white font-semibold">AI Tutor for every student</div>
                    <div className="text-sm text-[#B6B0C7]">Doubt-solving, quiz generation, revision plans — trained on your board's curriculum.</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#22D3EE]/15 text-[#22D3EE] flex items-center justify-center shrink-0"><Lightbulb size={16}/></div>
                  <div>
                    <div className="text-white font-semibold">AI Guidance for teachers</div>
                    <div className="text-sm text-[#B6B0C7]">Lesson plans, question-paper drafts, and personalised feedback for each student.</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#3B82F6]/15 text-[#3B82F6] flex items-center justify-center shrink-0"><ClipboardCheck size={16}/></div>
                  <div>
                    <div className="text-white font-semibold">AI Attendance & alerts</div>
                    <div className="text-sm text-[#B6B0C7]">Face-recognition attendance with instant absent-student alerts to parents.</div>
                  </div>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { k: "24×7", v: "Tutor availability" },
                { k: "8+", v: "Languages supported" },
                { k: "70%", v: "Time saved on lesson planning" },
                { k: "3s", v: "Doubt response time" },
              ].map((s) => (
                <div key={s.v} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="text-2xl md:text-3xl font-extrabold text-white">{s.k}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.18em] text-[#B6B0C7]">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="mt-24 max-w-4xl mx-auto px-6 md:px-10">
        <div className="text-center">
          <div className="eyebrow">Answers</div>
          <h2 className="mt-2 text-3xl md:text-5xl font-bold text-white tracking-tight">Frequently asked</h2>
        </div>
        <div className="mt-10 space-y-3" data-testid="sms-faq">
          {FAQ.map((item, i) => {
            const active = openFaq === i;
            return (
              <button
                key={item.q}
                onClick={() => setOpenFaq(active ? -1 : i)}
                className={`w-full text-left card p-5 transition-colors ${active ? "border-[#3B82F6]/40" : "hover:border-white/20"}`}
                data-testid={`sms-faq-${i}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="text-white font-semibold">{item.q}</div>
                  <span className={`shrink-0 text-[#3B82F6] transition-transform ${active ? "rotate-45" : ""}`}>+</span>
                </div>
                {active && (
                  <div className="mt-3 text-sm text-[#B6B0C7] leading-relaxed">{item.a}</div>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* ---------- Final CTA ---------- */}
      <section className="mt-24 max-w-7xl mx-auto px-6 md:px-10">
        <div className="card p-10 md:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-[0.1]" aria-hidden="true" style={{
            backgroundImage: "radial-gradient(circle at 50% 0, rgba(59,130,246,0.7) 0, transparent 50%)",
          }}/>
          <div className="relative">
            <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
              Ready to run your school on <span className="text-gradient">one platform?</span>
            </h2>
            <p className="mt-4 text-[#B6B0C7] max-w-2xl mx-auto">Book a 30-minute walkthrough with our team and see how ZeroLabs SMS fits your institute. Or jump straight into the live product.</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <a href={LAUNCH_URL} target="_blank" rel="noreferrer" className="btn-primary" data-testid="sms-final-launch">
                Try Live Demo <ExternalLink size={16}/>
              </a>
              <Link to="/contact#book" className="btn-secondary" data-testid="sms-final-book">
                <ArrowRight size={14}/> Book a Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Video lightbox ---------- */}
      {openVideo && (
        <div data-testid="sms-video-lightbox" className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setOpenVideo(false)}>
          <div className="relative w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpenVideo(false)} className="absolute -top-10 right-0 text-white/80 hover:text-white" aria-label="Close">
              <X size={22}/>
            </button>
            <iframe
              src={`${YT_EMBED}&autoplay=1`}
              title="School Management System full demo"
              data-testid="sms-video-lightbox-iframe"
              className="w-full h-full rounded-xl border border-white/10"
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}
